import React from 'react'
import styled from 'styled-components'

import { ExternalContent } from '@hozana/dom/components/ExternalContent'
import { IMAGE_SIZES } from '@hozana/styling/image'
import { QA } from '@hozana/tests/constants'
import { EVENT_ORIGIN, TRACKING_SOURCE } from '@hozana/tracking/constants'
import { GTM } from '@hozana/tracking/gtm'

import { Block } from 'elements/layout/Block'
import { Cell } from 'elements/layout/Cell'
import { Container } from 'elements/layout/Container'
import { Div } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'
import { Space } from 'elements/layout/Space'
import { VariableHeightImage } from 'elements/media/VariableHeightImage'
import { H } from 'elements/text/H'
import { P } from 'elements/text/P'
import { Chevron } from 'elements/ui/Chevron'
import { Separator } from 'elements/ui/Separator'

import { ICON } from 'config/icons'
import { Popover } from 'general/structure/Popover'

import { CommunityPrayer } from 'modules/community/components/CommunityPrayer'
import { CommunityThumbnail } from 'modules/community/components/CommunityThumbnail'
import type { TCommunity } from 'modules/community/types'
import { PublicationHeader } from 'modules/publication/components/PublicationHeader'
import { PublicationOptions } from 'modules/publication/components/PublicationOptions'
import type { TPublication } from 'modules/publication/types'

import { SharePublicationButtons } from './SharePublicationButtons'

export const PUBLICATION_MAX_WIDTH = '680px'

const StyledImage = styled(VariableHeightImage)`
  background-color: white;
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.3);
  @media (min-width: 696px) {
    border-radius: 5px;
    box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.3);
  }
`

type TPublicationPageContentProps = {
  publication: TPublication
  community: TCommunity
}

export const PublicationPageContent: TFC<TPublicationPageContentProps> = ({ publication, community }) => {
  const isScheduled = publication.isScheduled && !publication.published
  const isDraft = publication.isDraft

  return (
    <article>
      <Container as="aside" maxW={PUBLICATION_MAX_WIDTH} p={{ xs: '5px 5px 0', sm: '30px 10px 0' }}>
        <Div m={{ xs: '0 30px 0 0', sm: '0 35px 0 0' }}>
          <PublicationHeader communityId={community.id} />
        </Div>
        <Popover
          top="40px"
          right
          w={{ sm: '370px' }}
          triggerComponent={
            <Chevron
              icon={ICON.ELLIPSIS_VERTICAL}
              onClick={() => GTM.trackEvent(GTM.EVENTS.CHEVRON, { trackingSource: TRACKING_SOURCE.PUBLICATION })}
              label="trans:publication:word.options"
              ariaLabel="trans:publication:publication.options.open"
              top={{ xs: '10px', md: '25px' }}
            />
          }
        >
          <PublicationOptions
            publication={publication}
            community={community}
            showCommunityActions
            isScheduled={isScheduled}
            isDraft={isDraft}
          />
        </Popover>
      </Container>
      <Container as="header" p={{ xs: '5px 5px 0', sm: '30px 10px 0' }}>
        <H
          data-testid={QA.PUBLICATION_PAGE.TITLE}
          color="yellow"
          align="center"
          fontSize={{ xs: '1.6em', sm: '2em', md: '2.5em' }}
          m={{ xs: '10px 0 1em', sm: '1em 0 23px' }}
        >
          {publication.name}
        </H>
      </Container>
      <Container as="section" maxW="680px" p="0px 0 10px">
        {publication.picture && (
          <StyledImage
            data-testid={QA.PUBLICATION_PAGE.IMAGE}
            m="0 auto 2em"
            src={publication.picture}
            size={IMAGE_SIZES.publication.big_no_height_limit}
            alt="trans:publication:publication.image-alt"
          />
        )}
        {/* Publication content */}
        <Div p={{ xs: '0 1em 1em', md: '0px' }}>
          <section>
            <ExternalContent.Publication content={publication.content} options={{ publication }} />
            <Block noOverflow m={{ xs: '30px 0' }} p="1em">
              <P bold align={'center'} fontSize="20px" m="0 0 20px">
                trans:publication:community.form.prayer.title
              </P>
              <CommunityPrayer title={community.prayerName} content={community.prayerContent} />
            </Block>
          </section>
        </Div>
        {!isScheduled && !isDraft && (
          <aside>
            <SharePublicationButtons publication={publication} community={community} />
          </aside>
        )}
      </Container>
      {/* Section: Published in */}
      {isScheduled || isDraft ? (
        <Space h="100px" />
      ) : (
        community && (
          <Div as="aside" bg="white" noPrint>
            <Separator />
            <Container maxW="680px" p={{ xs: '1em 5px 20px', sm: '1em 10px 30px' }}>
              <H size="2" bold fontSize="1em" m="0 0 20px 0" color="black" font="openSans">
                trans:publication:words.published-in
              </H>
              <Row>
                <Cell flex="1">
                  <CommunityThumbnail communityId={community.id} eventOrigin={EVENT_ORIGIN.PUBLICATION_BOTTOM_BLOCK} />
                </Cell>
              </Row>
            </Container>
          </Div>
        )
      )}
    </article>
  )
}
